import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Badge,
  Button,
  Divider,
  Avatar,
  Center,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { 
  FaDollarSign, 
  FaClipboardList, 
  FaPhone,
  FaEnvelope,
  FaFileContract,
  FaGoogle,
  FaCreditCard,
  FaUser,
  FaRobot,
  FaSyncAlt,
  FaChartPie,
  FaHandsHelping,
  FaHeartbeat,
  FaUserClock,
  FaBrain,
  FaArrowRight,
  FaPlug,
  FaExclamationCircle,
} from "react-icons/fa";
import { MdTrackChanges } from "react-icons/md";
import { OverviewTag } from "../Shared/Tags"; // Import OverviewTag from Shared

// Simplified version of ActivityItem from Activities folder
const ActivityDisplay = ({ title, date, sender, content, source, icon, iconColor, isNew = false, logoSrc, className, ...rest }) => {
  const bgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  return (
    <Box
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      bg={isNew ? useColorModeValue("blue.50", "blue.900") : bgColor}
      mb={3}
      p={0}
      overflow="hidden"
      boxShadow="sm"
      position="relative"
      className={className}
      {...rest}
    >
      {/* Activity Header */}
      <Flex 
        p={3}
        borderBottomWidth="1px" 
        borderColor={borderColor}
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex alignItems="center">
          <Center
            bg={`${iconColor}.50`}
            borderRadius="md"
            p={2}
            mr={2}
            boxSize="36px"
            overflow="hidden"
          >
            {logoSrc ? (
              <Image src={logoSrc} alt={source} width="22px" height="22px" />
            ) : (
              <Icon as={icon} boxSize={4} color={`${iconColor}.500`} />
            )}
          </Center>
          
          <Box>
            <Flex alignItems="center">
              <Text fontWeight="semibold" color={textColor} mr={2} fontSize="sm">
              {title}
            </Text>
              {isNew && (
                <Badge 
                  colorScheme="blue" 
                  variant="solid"
                  fontSize="xs"
                >
                  NEW
                </Badge>
              )}
            </Flex>
            <Flex alignItems="center">
              <Text fontSize="sm" color={secondaryTextColor}>
                {date} • {source}
              </Text>
            </Flex>
          </Box>
        </Flex>
        
        <Flex alignItems="center">
          <Avatar size="xs" name={sender} mr={1} />
          <Text fontSize="sm" fontWeight="medium">
            {sender}
          </Text>
        </Flex>
      </Flex>
      
      {/* Activity Content */}
      <Box p={3}>
        <Text color={secondaryTextColor} fontSize="sm">
          {content}
        </Text>
      </Box>
    </Box>
  );
};

// Feature card for Hands-Free Deal Management section
const FeatureCard = ({ title, description, icon, color }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  return (
    <Box
      p={isMobile ? 3 : 4}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      bg={bgColor}
      height="100%"
      width="100%"
      transition="all 0.3s"
      boxShadow="sm"
      position="relative"
      zIndex={1}
      className="feature-card"
      _hover={{
        transform: "translateY(-3px)",
        boxShadow: "md",
        borderColor: `${color}.300`,
        zIndex: 10,
      }}
    >
      <VStack spacing={isMobile ? 2 : 3} align="flex-start">
        <Center
          bg={`${color}.100`}
          borderRadius="md"
          boxSize={isMobile ? "36px" : "44px"}
          color={`${color}.500`}
          borderWidth="1px"
          borderColor={`${color}.200`}
        >
          <Icon as={icon} boxSize={isMobile ? 4 : 5} />
        </Center>
        
        <Text 
          fontSize={isMobile ? "md" : "lg"} 
          fontWeight="semibold"
          color={textColor}
        >
          {title}
        </Text>
        
        <Text fontSize={isMobile ? "xs" : "md"} color={secondaryTextColor}>
          {description}
        </Text>
      </VStack>
    </Box>
  );
};

// CSS-only carousel component for mobile
const ScrollableCarousel = ({ children, itemWidth = "280px", gap = "12px", autoScroll = false, speed = "medium", rows = 1 }) => {
  const carouselRef = useRef(null);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [isVisible, setIsVisible] = useState(false);
  
  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }
    
    return () => {
      if (carouselRef.current) {
        observer.unobserve(carouselRef.current);
      }
    };
  }, []);
  
  // Auto-scroll effect for mobile - only when visible
  useEffect(() => {
    if (!isMobile || !carouselRef.current || !autoScroll || !isVisible) return;
    
    const carousel = carouselRef.current; 
    let scrollInterval;
    let direction = 1; // 1 for right, -1 for left
    
    // Determine scroll speed (pixels per interval)
    const getScrollSpeed = () => {
      switch(speed) {
        case "slow": return 0.5;
        case "medium": return 1;
        case "fast": return 2;
        default: return 1;
      }
    };
    
    // Start the continuous scroll with direction
    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (!carousel) return;
        
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        
        // Use direction to determine scroll direction
        // If we reach the end, change direction
        if (carousel.scrollLeft >= maxScroll - 5) {
          // Using scrollTo with behavior: smooth causes a visible jump
          // Instead, we'll gradually scroll back to start
          carousel.classList.add('no-transition');
          carousel.scrollLeft = 0;
          // Force a reflow to ensure the no-transition class takes effect
          void carousel.offsetWidth;
          carousel.classList.remove('no-transition');
        } else {
          // Otherwise continue scrolling in the current direction
          carousel.scrollLeft += getScrollSpeed() * direction;
        }
      }, 20); // Smaller interval for smoother animation
    };
    
    const stopAutoScroll = () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
    
    // Start auto-scroll after component mounts
    const timer = setTimeout(startAutoScroll, 1000);
    
    // Event listeners to pause auto-scroll when user interacts
    const handleTouchStart = () => {
      stopAutoScroll();
    };
    
    const handleTouchEnd = () => {
      // Only restart if the component is still visible
      if (isVisible) {
        setTimeout(startAutoScroll, 3000);
      }
    };
    
    carousel.addEventListener('touchstart', handleTouchStart);
    carousel.addEventListener('touchend', handleTouchEnd);
    carousel.addEventListener('mousedown', handleTouchStart);
    carousel.addEventListener('mouseup', handleTouchEnd);
    
    return () => {
      stopAutoScroll();
      clearTimeout(timer);
      
      if (carousel) {
        carousel.removeEventListener('touchstart', handleTouchStart);
        carousel.removeEventListener('touchend', handleTouchEnd);
        carousel.removeEventListener('mousedown', handleTouchStart);
        carousel.removeEventListener('mouseup', handleTouchEnd);
      }
    };
  }, [isMobile, autoScroll, speed, isVisible]);
  
  // Create row groups if multiple rows are specified
  const createRowGroups = () => {
    if (rows <= 1) {
      return (
        <Flex gap={gap}>
          {React.Children.map(children, (child, index) => (
            <Box 
              key={index}
              flex={`0 0 ${itemWidth}`}
              className="carousel-item"
            >
              {child}
            </Box>
          ))}
          {/* Add duplicate items for seamless looping */}
          {React.Children.map(children, (child, index) => (
            <Box 
              key={`clone-${index}`}
              flex={`0 0 ${itemWidth}`}
              className="carousel-item clone-item"
            >
              {child}
            </Box>
          ))}
        </Flex>
      );
    }
    
    // For multiple rows, split the children into rows
    const childrenArray = React.Children.toArray(children);
    const itemsPerRow = Math.ceil(childrenArray.length / rows);
    
    return (
      <VStack spacing={gap} align="stretch">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Flex key={`row-${rowIndex}`} gap={gap}>
            {childrenArray
              .slice(rowIndex * itemsPerRow, (rowIndex + 1) * itemsPerRow)
              .map((child, index) => (
                <Box 
                  key={`${rowIndex}-${index}`}
                  flex={`0 0 ${itemWidth}`}
                  className="carousel-item"
                >
                  {child}
                </Box>
              ))}
            {/* Always add duplicate items for seamless looping */}
            {childrenArray
              .slice(rowIndex * itemsPerRow, (rowIndex + 1) * itemsPerRow)
              .map((child, index) => (
                <Box 
                  key={`clone-${rowIndex}-${index}`}
                  flex={`0 0 ${itemWidth}`}
                  className="carousel-item clone-item"
                >
                  {child}
                </Box>
              ))}
          </Flex>
        ))}
      </VStack>
    );
  };
  
  return (
    <Box 
      ref={carouselRef}
      width="100%" 
      overflowX="auto" 
      className="scrollable-carousel"
      sx={{
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { display: "none" },
      }}
      pb={4}
      pt={3}
      px={1}
    >
      {createRowGroups()}
    </Box>
  );
};

// DataSourcesGrid modified to use scroll carousel for mobile
const DataSourcesGrid = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Get style for logos - using color accents instead of monochrome
  const getLogoStyle = (name) => {
    const baseStyle = {
      maxWidth: "100%",
      maxHeight: "100%",
      filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.1))",
    };
    
    // Specific adjustments for certain logos
    switch(name) {
      case "Slack":
        return {
          ...baseStyle,
          width: "24px",
          height: "24px",
        };
      case "WhatsApp":
      case "Teams":
      case "PandaDoc":
        return {
          ...baseStyle,
          width: "26px",
          height: "26px",
        };
      case "Google Analytics":
      case "Amplitude":
      case "Google Drive":
        return {
          ...baseStyle,
          width: "28px",
          height: "28px",
        };
      default:
        return baseStyle;
    }
  };

  // Custom background colors that match the brand colors
  const getIconBgColor = (name) => {
    switch(name) {
      case "Fireflies":
        return "#EEFDF4";
      case "Gmail":
        return "#F5F0F0";
      case "Slack":
        return "#FCF3FF";
      case "Google Calendar":
        return "#E8F0FE";
      case "Microsoft Teams":
        return "#E3F1FF";
      case "Zoom":
        return "#DEEEFF";
      case "PostHog":
        return "#FFF4E6";
      case "Google Analytics":
        return "#FFF8E0";
      case "Amplitude":
        return "#E4F7FF";
      case "Google Drive":
        return "#FEEAE8";
      case "PandaDoc":
        return "#E6F9F7";
      case "Stripe":
        return "#F4E9FF";
      case "Zapier":
        return "#FFF1E7";
      case "Typeform":
        return "#FFF0F5";
      case "WhatsApp":
        return "#E7F7EF";
      case "Jira":
        return "#E6F0FF";
      default:
        return "#F5F5F5";
    }
  };

  // Organize data sources in a way that ensures balanced rows
  // For a grid with 6, 4, or 2 columns, we need a number of items divisible by 6
  const dataSources = [
    {
      name: "Gmail",
      category: "Messages",
      logo: "/Gmail.svg",
      color: "blue",
    },
    {
      name: "Slack",
      category: "Communication",
      logo: "/Slack.svg",
      color: "purple",
    },
    {
      name: "Microsoft Teams",
      category: "Collaboration",
      logo: "/Teams.svg",
      color: "blue",
    },
    {
      name: "WhatsApp",
      category: "Messaging",
      logo: "/WhatsApp.svg", 
      color: "green",
    },
    {
      name: "Zoom",
      category: "Video Conferencing",
      logo: "/zoom.svg",
      color: "blue",
    },
    {
      name: "Fireflies",
      category: "Calls",
      logo: "/fireflies.svg",
      color: "green",
    },
    {
      name: "Google Calendar",
      category: "Scheduling",
      logo: "/Google_Calendar.svg",
      color: "blue",
    },
    {
      name: "Google Drive",
      category: "Documents",
      logo: "/Google_Drive.svg",
      color: "red",
    },
    {
      name: "PandaDoc",
      category: "Contracts",
      logo: "/idEsZ-mbVh_1742145481326.svg",
      color: "teal",
    },
    {
      name: "Stripe",
      category: "Billing",
      logo: "/Stripe.svg",
      color: "purple",
    },
    {
      name: "Google Analytics",
      category: "Web Analytics",
      logo: "/google-analytics.svg",
      color: "yellow",
    },
    {
      name: "PostHog",
      category: "Product Analytics",
      logo: "/posthog.svg",
      color: "orange",
    },
    // Added a second row that looks balanced
    {
      name: "Amplitude",
      category: "Analytics",
      logo: "/amplitude.svg",
      color: "blue",
    },
    {
      name: "Zapier",
      category: "Automation",
      logo: "/zapier.svg",
      color: "orange",
    },
    {
      name: "Typeform",
      category: "Forms",
      logo: "/Typeform.svg",
      color: "pink",
    },
    {
      name: "Jira",
      category: "Project Management",
      logo: "/jira_color.svg",
      color: "blue",
    },
    // We need 18 items for perfect rows on all screen sizes (divisible by 6, 4, and 2)
    {
      name: "Google Meet",
      category: "Video Calls",
      logo: "/google-meet.svg",
      color: "green",
    },
    {
      name: "Google Docs",
      category: "Documents",
      logo: "/google-docs.svg",
      color: "blue",
    },
  ];

  const renderSourceCard = (source) => (
    <Box
      p={{ base: 3, md: 4 }}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      bg={bgColor}
      transition="all 0.3s"
      boxShadow="sm"
      height="100%"
      width="100%"
      _hover={{
        boxShadow: "md",
        transform: "translateY(-3px)",
        borderColor: `${source.color}.300`,
        zIndex: 10,
      }}
      position="relative"
    >
      <Flex direction="column" align="center" textAlign="center" justify="center" height="100%">
        <Center
          bg={getIconBgColor(source.name)}
          borderRadius="full"
          p={3}
          mb={3}
          width={{ base: "50px", md: "56px" }}
          height={{ base: "50px", md: "56px" }}
        >
          <Box
            className="svg-container"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={{ base: "28px", md: "30px" }}
            height={{ base: "28px", md: "30px" }}
          >
            <Image 
              src={source.logo} 
              alt={source.name}
              className="colored-svg"
              style={getLogoStyle(source.name)}
            />
          </Box>
        </Center>
        <Text fontWeight="semibold" color={textColor} fontSize="sm" mb={1}>
          {source.name}
        </Text>
        <Text fontSize="xs" color={secondaryTextColor}>
          {source.category}
        </Text>
      </Flex>
    </Box>
  );

  // Calculate a consistent square size for the carousel items
  const squareSize = "140px";  // This ensures items remain square
  
  return (
    <Box width="100%">
      {isMobile ? (
        <ScrollableCarousel 
          itemWidth={squareSize} 
          gap="12px" 
          autoScroll={true} 
          speed="medium"
          rows={2}
        >
          {dataSources.map((source) => (
            <Box 
              key={source.name} 
              height={squareSize}
              width={squareSize}
              aspectRatio="1/1"
              position="relative"
              className="carousel-card-wrapper"
            >
              {renderSourceCard(source)}
            </Box>
          ))}
        </ScrollableCarousel>
      ) : (
        <Grid
          templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }}
          gap={{ base: 3, md: 4, lg: 5 }}
          justifyContent="center"
          mx="auto"
          py={2}
        >
          {dataSources.map((source) => (
            <GridItem key={source.name}>
              {renderSourceCard(source)}
            </GridItem>
          ))}
        </Grid>
      )}
    </Box>
  );
};

// Snap-scrolling carousel for the "Look Ma, No Hands!" section
export const SnapScrollCarousel = ({ children, autoPlayInterval = 3000 }) => {
  const carouselRef = useRef(null);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Add visibility tracking
  const [isVisible, setIsVisible] = useState(false);
  
  // Set up measurement function
  const logDimensions = useCallback(() => {
    if (!carouselRef.current) return;
    
    const carousel = carouselRef.current;
    const items = carousel.querySelectorAll(".snap-carousel-item");
    setTotalItems(items.length);
  }, []);
  
  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }
    
    return () => {
      if (carouselRef.current) {
        observer.unobserve(carouselRef.current);
      }
    };
  }, []);
  
  // Run measurements on load and resize
  useEffect(() => {
    logDimensions();
    
    window.addEventListener('resize', logDimensions);
    
    return () => {
      window.removeEventListener('resize', logDimensions);
    };
  }, [logDimensions]);
  
  const scrollToNext = useCallback(() => {
    if (!carouselRef.current || isTransitioning) return;
    
    setIsTransitioning(true);
    
    let nextIndex = (currentIndex + 1) % totalItems;
    setCurrentIndex(nextIndex);
    
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500); // Match this timing with CSS transition duration
  }, [currentIndex, totalItems, isTransitioning]);
  
  const scrollToPrev = () => {
    if (!carouselRef.current || isTransitioning) return;
    
    setIsTransitioning(true);
    
    let prevIndex = (currentIndex - 1 + totalItems) % totalItems;
    setCurrentIndex(prevIndex);
    
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500); // Match this timing with CSS transition duration
  };
  
  // Auto-scroll for mobile - only when visible
  useEffect(() => {
    if (!isMobile || !isVisible) return;
    
    const interval = setInterval(() => {
      scrollToNext();
    }, autoPlayInterval);
    
    return () => clearInterval(interval);
  }, [isMobile, scrollToNext, autoPlayInterval, isVisible]);
  
  // Add navigation controls
  const NavButton = ({ direction, onClick }) => (
    <Box
      position="absolute"
      top="50%"
      transform="translateY(-50%)"
      left={direction === "prev" ? 0 : "auto"}
      right={direction === "next" ? 0 : "auto"}
      bg="white"
      color="gray.800"
      borderRadius="full"
      width="36px"
      height="36px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      boxShadow="md"
      onClick={onClick}
      zIndex={2}
      ml={direction === "prev" ? 2 : 0}
      mr={direction === "next" ? 2 : 0}
      opacity={{ base: 0, md: 0.7 }}
      _hover={{ opacity: 1 }}
      transition="opacity 0.2s ease"
      className="snap-carousel-nav"
    >
      <Icon 
        as={FaArrowRight} 
        transform={direction === "prev" ? "rotate(180deg)" : "none"}
        boxSize={4}
      />
    </Box>
  );

  return (
    <Box 
      className="snap-carousel-container" 
      position="relative"
    >
      <Box className="snap-carousel" ref={carouselRef}>
        <VStack
          className="snap-carousel-track"
          spacing={0}
          transform={`translateX(-${currentIndex * 100}%)`}
          transition="transform 0.4s ease"
        >
          {React.Children.map(children, (child, index) => (
            <Box
              className="snap-carousel-item"
              key={index}
              width="100%"
              flexShrink={0}
              transition="all 0.3s"
            >
              <Box className="carousel-item-inner">
                {child}
              </Box>
            </Box>
          ))}
        </VStack>
      </Box>
      
      {/* Only show navigation controls on desktop */}
      {!isMobile && totalItems > 1 && (
        <>
          <NavButton direction="prev" onClick={scrollToPrev} />
          <NavButton direction="next" onClick={scrollToNext} />
        </>
      )}
    </Box>
  );
};

// HandsFreeFeatures modified to use snap-scrolling carousel on mobile
const HandsFreeFeatures = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const featuresRef = useRef([
    {
      title: "Real-time Deal Updates",
      description: "Automatically capture and update deals from emails and calls without manual data entry.",
      icon: FaSyncAlt,
      color: "blue",
    },
    {
      title: "Stage Progression",
      description: "Update deal stages automatically based on AI analysis of conversations and document exchanges.",
      icon: FaClipboardList,
      color: "orange",
    },
    {
      title: "Conversation Analysis & Scoring",
      description: "Score communication to identify high-value opportunities and prioritize follow-ups.",
      icon: FaRobot,
      color: "purple",
    },
    {
      title: "Zero Manual Updates",
      description: "Keep deal data current without requiring CRM updates or activity logging.",
      icon: FaHandsHelping,
      color: "green",
    }
  ]);

  // Log feature card dimensions
  useEffect(() => {
    if (isMobile) {
      setTimeout(() => {
        const featureCards = document.querySelectorAll('.feature-card');
        const features = featuresRef.current;
        featureCards.forEach((card, i) => {
          if (i < features.length) {
            // Remove console.log statement
          }
        });
      }, 1000);
    }
  }, [isMobile]);

  if (isMobile) {
    return (
      <Grid templateColumns="repeat(2, 1fr)" gap={3} py={2}>
        {featuresRef.current.map((feature, index) => (
          <GridItem key={index} height="100%">
            <FeatureCard
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
              color={feature.color}
            />
          </GridItem>
        ))}
      </Grid>
    );
  }

  return (
    <VStack spacing={2.5} height="100%" py={2}>
      {featuresRef.current.map((feature, index) => (
        <FeatureCard
          key={index}
          title={feature.title}
          description={feature.description}
          icon={feature.icon}
          color={feature.color}
        />
      ))}
    </VStack>
  );
};

// AnalyticsFeatures modified to use scroll carousel on mobile
const AnalyticsFeatures = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const features = [
    {
      title: "From First Touch to Lasting Relationship",
      description: "Capture every interaction across channels to unlock the full story of your customer's experience.",
      icon: FaUserClock,
      color: "blue",
    },
    {
      title: "Automatic Churn Detection",
      description: "Identify at-risk customers early through AI-powered analysis of engagement patterns, product usage, and communication sentiment.",
      icon: FaBrain,
      color: "purple",
    },
    {
      title: "Relationship Health Indicators",
      description: "Monitor real-time metrics showing the health of every customer relationship with actionable insights to improve engagement.",
      icon: FaChartPie,
      color: "green",
    },
    {
      title: "Data Integration Hub",
      description: "Connect with your existing analytics tools to centralize customer insights and create a comprehensive view of each relationship.",
      icon: FaPlug,
      color: "teal",
    }
  ];

  if (isMobile) {
    return (
      <Grid templateColumns="repeat(2, 1fr)" gap={3} py={2}>
        {features.map((feature, index) => (
          <GridItem key={index} height="100%">
            <FeatureCard
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
              color={feature.color}
            />
          </GridItem>
        ))}
      </Grid>
    );
  }

  return (
    <VStack spacing={2.5} height="100%" py={2}>
      {features.map((feature, index) => (
        <FeatureCard
          key={index}
          title={feature.title}
          description={feature.description}
          icon={feature.icon}
          color={feature.color}
        />
      ))}
    </VStack>
  );
};

// Animated Deal Summary Card
const AnimatedDealSummaryCard = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const highlightBg = useColorModeValue("blue.50", "blue.900");

  // Animation states
  const [currentStage, setCurrentStage] = useState(0);
  const [dealScore, setDealScore] = useState(65);
  const [dealAmount, setDealAmount] = useState("$45,000");
  const [dealStage, setDealStage] = useState("Discovery");
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState("TechStart is evaluating our enterprise plan. Initial call completed with positive feedback. Awaiting next steps.");
  const [lastUpdated, setLastUpdated] = useState("2 hours ago");
  // Add a ref for the card and a state for visibility
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Animation for the badge
  const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `;
  const pulsing = `${pulseAnimation} 2s infinite`;
  
  // Define deal stages and corresponding activities
  const dealStages = [
    {
      stage: "Discovery",
      score: 65,
      amount: "$45,000",
      summary: "TechStart is evaluating our enterprise plan. Initial call completed with positive feedback. Awaiting next steps.",
      activities: [
        {
          type: "call",
          title: "Sales Call",
          date: "2 hours ago",
          sender: "David Chen",
          content: "Call summary: Discussed product overview and requirements. TechStart team expressed interest in enterprise plan for their team of 25 users.",
          source: "Fireflies",
          icon: FaPhone,
          iconColor: "green",
          logoSrc: "/fireflies.svg",
          isNew: true
        },
        {
          type: "email",
          title: "Initial Outreach",
          date: "2 days ago",
          sender: "Alex Johnson",
          content: "Thank you for your interest in our enterprise solution. I'd like to schedule a call to discuss your requirements in more detail.",
          source: "Gmail",
          icon: FaEnvelope,
          iconColor: "blue",
          logoSrc: "/Gmail.svg",
          isNew: false
        }
      ]
    },
    {
      stage: "Qualification",
      score: 72,
      amount: "$45,000",
      summary: "TechStart enterprise plan discussion advancing. Budget approval process initiated internally on their side. Preparing detailed proposal.",
      activities: [
        {
          type: "email",
          title: "Budget Discussion",
          date: "Just now",
          sender: "Sarah Miller",
          content: "Thanks for the product demo! Our team was impressed with the features. I'm initiating the budget approval process for the enterprise plan. Could you send a detailed proposal for our finance team?",
          source: "Gmail",
          icon: FaEnvelope,
          iconColor: "blue",
          logoSrc: "/Gmail.svg",
          isNew: true
        },
        {
          type: "call",
          title: "Sales Call",
          date: "2 days ago",
          sender: "David Chen",
          content: "Call summary: Discussed product overview and requirements. TechStart team expressed interest in enterprise plan for their team of 25 users.",
          source: "Fireflies",
          icon: FaPhone,
          iconColor: "green",
          logoSrc: "/fireflies.svg",
          isNew: false
        }
      ]
    },
    {
      stage: "Proposal",
      score: 78,
      amount: "$48,500",
      summary: "Proposal sent to TechStart with enterprise pricing. Technical team demo scheduled. Budget appears to be approved based on latest communication.",
      activities: [
        {
          type: "document",
          title: "Proposal Shared",
          date: "Just now",
          sender: "Michael Johnson",
          content: "The enterprise proposal has been shared with TechStart's team. Includes pricing for 25 users with premium support package and annual payment discount.",
          source: "Google Drive",
          icon: FaGoogle,
          iconColor: "red",
          logoSrc: "/Google_Drive.svg",
          isNew: true
        },
        {
          type: "email",
          title: "Budget Discussion",
          date: "2 days ago",
          sender: "Sarah Miller",
          content: "Thanks for the product demo! Our team was impressed with the features. I'm initiating the budget approval process for the enterprise plan. Could you send a detailed proposal for our finance team?",
          source: "Gmail",
          icon: FaEnvelope,
          iconColor: "blue",
          logoSrc: "/Gmail.svg",
          isNew: false
        }
      ]
    },
    {
      stage: "Negotiation",
      score: 86,
      amount: "$52,000",
      summary: "TechStart negotiating for additional services. Legal requested security documentation. Technical integration questions resolved in last conversation.",
      activities: [
        {
          type: "meeting",
          title: "Technical Review",
          date: "Just now",
          sender: "Jessica Lee",
          content: "Meeting summary: Technical team approved integration plan. Security team has requested additional compliance documentation. They've requested 2 additional admin licenses, bringing total to $52,000.",
          source: "Zoom",
          icon: FaPhone,
          iconColor: "blue",
          logoSrc: "/zoom.svg",
          isNew: true
        },
        {
          type: "document",
          title: "Proposal Shared",
          date: "3 days ago",
          sender: "Michael Johnson",
          content: "The enterprise proposal has been shared with TechStart's team. Includes pricing for 25 users with premium support package and annual payment discount.",
          source: "Google Drive",
          icon: FaGoogle,
          iconColor: "red",
          logoSrc: "/Google_Drive.svg",
          isNew: false
        }
      ]
    },
    {
      stage: "Contract",
      score: 92,
      amount: "$52,000",
      summary: "Contract sent to TechStart. Legal review in progress. Implementation timeline confirmed for Q3 rollout. Final signatures expected soon.",
      activities: [
        {
          type: "contract",
          title: "Contract Sent",
          date: "Just now",
          sender: "Thomas Anderson",
          content: "The final contract has been sent to TechStart for review and signature. Includes all negotiated terms, implementation timeline, and annual billing cycle.",
          source: "PandaDoc",
          icon: FaFileContract,
          iconColor: "teal",
          logoSrc: "/idEsZ-mbVh_1742145481326.svg",
          isNew: true
        },
        {
          type: "meeting",
          title: "Technical Review",
          date: "2 days ago",
          sender: "Jessica Lee",
          content: "Meeting summary: Technical team approved integration plan. Security team has requested additional compliance documentation. They've requested 2 additional admin licenses, bringing total to $52,000.",
          source: "Zoom",
          icon: FaPhone,
          iconColor: "blue",
          logoSrc: "/zoom.svg",
          isNew: false
        }
      ]
    },
    {
      stage: "Closed Won",
      score: 100,
      amount: "$52,000",
      summary: "TechStart deal closed! Contract signed and payment processed. Implementation scheduled to begin next week. Account handoff to customer success team in progress.",
      activities: [
        {
          type: "payment",
          title: "Payment Processed",
          date: "Just now",
          sender: "Finance Team",
          content: "TechStart annual payment has been processed successfully. $52,000 received for enterprise plan with 27 licenses. Implementation team has been notified to begin onboarding.",
          source: "Stripe",
          icon: FaCreditCard,
          iconColor: "purple",
          logoSrc: "/Stripe.svg",
          isNew: true
        },
        {
          type: "contract",
          title: "Contract Sent",
          date: "1 week ago",
          sender: "Thomas Anderson",
          content: "The final contract has been sent to TechStart for review and signature. Includes all negotiated terms, implementation timeline, and annual billing cycle.",
          source: "PandaDoc",
          icon: FaFileContract,
          iconColor: "teal",
          logoSrc: "/idEsZ-mbVh_1742145481326.svg",
          isNew: false
        }
      ]
    }
  ];

  // Set up intersection observer to detect when component is in viewport
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // trigger when at least 10% of the element is visible
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  // Function to animate deal progression - only run when visible
  useEffect(() => {
    if (!isVisible) return; // Skip animation if not visible
    
    // Initialize with the first stage's activities
    setActivities(dealStages[0].activities.map(activity => ({
      ...activity,
      key: `activity-0-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    })));
    
    // Counter for stages
    let stageCounter = 0;
    
    // Create interval to cycle through stages
    const interval = setInterval(() => {
      stageCounter++;
      const newStage = stageCounter % dealStages.length;
      
      // Update state with new stage information
      setCurrentStage(newStage);
      setDealStage(dealStages[newStage].stage);
      setDealScore(dealStages[newStage].score);
      setDealAmount(dealStages[newStage].amount);
      setSummary(dealStages[newStage].summary);
      setLastUpdated("Just now");
      
      // Update activities with new activities
      setActivities(dealStages[newStage].activities.map(activity => ({
        ...activity,
        key: `activity-${newStage}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      })));
      
    }, 4000); // Change stage every 4 seconds

    // Cleanup interval on component unmount or when not visible
    return () => {
      clearInterval(interval);
    };
  }, [isVisible]); // Add isVisible as a dependency

  return (
    <Box
      ref={cardRef}
      width="100%"
      bg={bgColor}
      p={6}
      borderRadius="md"
      boxShadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        borderColor: "gray.300",
        boxShadow: "lg",
        transform: "translateY(-2px)",
      }}
    >
      {/* Header with title and real-time badge on the same row */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Flex align="center">
          <Center
            bg="blue.50"
            borderRadius="md"
            mr={3}
            borderWidth="1px"
            borderColor="blue.200"
            boxSize="36px"
          >
            <Icon as={MdTrackChanges} color="blue.500" boxSize={5} />
          </Center>
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            TechStart Deal
          </Text>
        </Flex>
        <Badge colorScheme="blue" fontSize="sm" borderRadius="md" px={2} py={1}>
          <Flex align="center">
            <Icon as={FaSyncAlt} mr={1} animation={isVisible ? pulsing : "none"} />
            <Text>REAL-TIME</Text>
          </Flex>
        </Badge>
      </Flex>
      
      <Text fontSize="sm" color="green.500" fontWeight="medium" mb={4}>
        Last updated automatically just now
      </Text>

      <VStack spacing={4} align="stretch">
        {/* Using OverviewTag component from Shared */}
        <HStack spacing={2} flexWrap="wrap">
          <OverviewTag
            icon={FaDollarSign}
            label="Amount"
            value={dealAmount}
            colorScheme="purple"
          />
          <OverviewTag
            icon={FaClipboardList}
            label="Stage"
            value={dealStage}
            colorScheme="orange"
          />
          <OverviewTag
            icon={FaChartPie}
            label="Deal Score"
            value={`${dealScore}%`}
            colorScheme={dealScore >= 90 ? "green" : dealScore >= 70 ? "orange" : "blue"}
          />
        </HStack>

        <Box pt={2}>
          <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2}>
            AI Summary
          </Text>
          <Box
            bg={highlightBg}
            p={4}
            borderRadius="md"
            fontSize="sm"
            color={secondaryTextColor}
            key={`summary-${currentStage}`} // Force re-render when summary changes
            transition="all 0.3s ease"
          >
            <Text mb={2}>
              {summary}
            </Text>
            <Divider my={2} borderColor={borderColor} />
            <Text fontStyle="italic" fontSize="xs">
              Last updated: {lastUpdated} • Sources: Gmail, Fireflies, Google Drive, Zoom, PandaDoc
            </Text>
          </Box>
        </Box>

        <Box pt={2}>
          <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={3}>
            Deal Activities
          </Text>
          
          {/* Activity Items - Same style as in Acme Corp Account */}
          {activities.map((activity) => (
            <ActivityDisplay 
              key={activity.key}
              title={activity.title}
              date={activity.date}
              sender={activity.sender}
              content={activity.content}
              source={activity.source}
              icon={activity.icon}
              iconColor={activity.iconColor}
              isNew={activity.isNew}
              logoSrc={activity.logoSrc}
            />
          ))}
        </Box>
      </VStack>
    </Box>
  );
};

// Churn Analytics Visual component that pairs with AnalyticsFeatures
const ChurnAnalyticsVisual = () => {
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const highlightBg = useColorModeValue("red.50", "red.900");
  
  // Add state and ref for visibility detection
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  
  // Animation for the alert badge
  const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `;
  const pulsing = `${pulseAnimation} 2s infinite`;

  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  return (
    <Box
      ref={cardRef}
      bg={bgColor}
      p={6}
      borderRadius="md"
      boxShadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        borderColor: "gray.300",
        boxShadow: "lg",
        transform: "translateY(-2px)",
      }}
    >
      {/* Header with title and alert badge */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Flex align="center">
          <Center
            bg="red.50"
            borderRadius="md"
            mr={3}
            borderWidth="1px"
            borderColor="red.200"
            boxSize="36px"
          >
            <Icon as={FaHeartbeat} color="red.500" boxSize={5} />
          </Center>
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            Acme Corp Account
          </Text>
        </Flex>
        <Badge colorScheme="red" fontSize="sm" borderRadius="md" px={2} py={1}>
          <Flex align="center">
            <Icon as={FaExclamationCircle} mr={1} animation={isVisible ? pulsing : "none"} />
            <Text>AT RISK</Text>
          </Flex>
        </Badge>
      </Flex>
      
      <Text fontSize="sm" color="red.500" fontWeight="medium" mb={4}>
        Last updated automatically just now
      </Text>

      <VStack spacing={4} align="stretch">
        {/* Using OverviewTag component from Shared */}
        <HStack spacing={2} flexWrap="wrap">
          <OverviewTag
            icon={FaDollarSign}
            label="ARR"
            value="$52,000"
            colorScheme="purple"
          />
          <OverviewTag
            icon={FaClipboardList}
            label="Stage"
            value="Renewal"
            colorScheme="orange"
          />
          <OverviewTag
            icon={FaChartPie}
            label="Health Score"
            value="23%"
            colorScheme="red"
          />
        </HStack>

        <Box pt={2}>
          <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2}>
            AI Summary
          </Text>
          <Box
            bg={highlightBg}
            p={4}
            borderRadius="md"
            fontSize="sm"
            color={secondaryTextColor}
            cursor="pointer"
            _hover={{
              boxShadow: "sm",
              bg: useColorModeValue("gray.50", "gray.700")
            }}
          >
            <Flex align="center" mb={3}>
              <Avatar size="sm" name="Acme Corp" src="" mr={2} />
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                  Critical Customer Alert
                </Text>
                <Text fontSize="xs" color="red.600" fontWeight="medium" mt={1}>
                  High churn risk • Renewal in 30 days
                </Text>
              </Box>
            </Flex>
            
            <Text mb={3}>
              Acme Corp hasn&apos;t used the product in 8 days. Usage has dropped 89% in the last 30 days. Their renewal is coming up in 30 days and there have been no engagement with renewal emails.
            </Text>
            
            <Button 
              size="sm"
              colorScheme="blue"
              rightIcon={<Icon as={FaArrowRight} fontSize="xs" />}
              _hover={{
                transform: "translateX(2px)",
              }}
              transition="transform 0.2s"
              width="100%"
            >
              Follow up now
            </Button>
            
            <Divider my={2} borderColor={borderColor} />
            <Text fontStyle="italic" fontSize="xs">
              Last updated: Just now • Sources: Product Usage, CRM, Email, Customer Success
            </Text>
          </Box>
        </Box>

        <Box pt={2}>
          <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={3}>
            Customer Activities
          </Text>
          
          {/* Activity Items - Updated to reverse chronological order */}
          <ActivityDisplay 
            title="Renewal Email"
            date="3 days ago"
            sender="Sales Team"
            content="Renewal notification email sent. No opens or clicks detected."
            source="Gmail"
            icon={FaEnvelope}
            iconColor="blue"
            logoSrc="/Gmail.svg"
          />
          
          <ActivityDisplay 
            title="Last Login Activity"
            date="8 days ago"
            sender="John Smith"
            content="Last active user login was 8 days ago. Previously averaged 12 logins per week."
            source="Product Analytics"
            icon={FaUser}
            iconColor="red"
          />
        </Box>
      </VStack>
    </Box>
  );
};

// Reorganized LandingDealSection with side-by-side layout for features and demo
const LandingDealSection = () => {
  const bgColor = useColorModeValue("white", "gray.900");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box bg={bgColor} py={20}>
      <Container maxW="1400px" px={{ base: 2, md: 3 }}>
        <VStack align="stretch">
          {/* Headline Section */}
          <VStack spacing={5} align="center" textAlign="center">
            <Flex
              bg="blue.50"
              borderRadius="full"
              p={2}
              alignItems="center"
              justifyContent="center"
              boxSize="60px"
              mb={2}
            >
              <Icon as={MdTrackChanges} color="blue.500" boxSize={8} />
            </Flex>
            
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color={headingColor}
            >
              Unified Revenue Intelligence Hub
            </Heading>
            
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              maxW="800px"
              color={textColor}
            >
              Aggregate data from your entire tech stack into real-time insights that empower your revenue team to win more deals.
            </Text>
          </VStack>

          {/* Data Sources Grid */}
          <Box py={{ base: 8, md: 12 }}>
            <VStack spacing={3} align="center" textAlign="center" mb={5}>
              <Flex
                bg="teal.50"
                borderRadius="full"
                p={2}
                alignItems="center"
                justifyContent="center"
                boxSize="60px"
                mb={2}
              >
                <Icon as={FaPlug} color="teal.500" boxSize={8} />
              </Flex>
              
              <Heading
                as="h3"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold" 
                color={headingColor} 
              >
                Something for Everyone 
              </Heading>
              
              <Text
                fontSize={{ base: "md", md: "lg" }}
                maxW="800px"
                color={textColor}
                pb={6}
              >
                Leverage data from all your tools in one centralized platform.
              </Text>
            </VStack>
            <Box
              mx="auto"
              width="100%"
              maxW="1200px"
              px={{ base: 0, md: 8 }}
            >
              <DataSourcesGrid />
            </Box>
          </Box>

          {/* Hands-Free Deal Management - Reorganized for side-by-side layout */}
          <Box>
            <VStack spacing={3} align="center" textAlign="center" mb={5}>
              <Flex
                bg="purple.50"
                borderRadius="full"
                p={2}
                alignItems="center"
                justifyContent="center"
                boxSize="60px"
                mb={2}
              >
                <Icon as={FaHandsHelping} color="purple.500" boxSize={8} />
              </Flex>
              
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={headingColor}
              >
                Look Ma, No Hands!
              </Heading>
              
              <Text
                fontSize={{ base: "md", md: "lg" }}
                maxW="800px"
                color={textColor}
                pb={6}
              >
                Lysto autonomously captures and analyzes every customer conversation, contract, and payment, providing a real-time view of deal progression and risk factors.
              </Text>
            </VStack>
            
            {/* Side-by-side layout for features and demo */}
            <Grid
              templateColumns={{ base: "1fr", lg: "0.9fr 1.1fr" }}
              gap={3}
              maxW="1200px"
              mx="auto"
              px={{ base: 2, md: 0 }}
            >
              <GridItem display="flex" alignItems="stretch">
                <Box flex="1" pr={{ lg: 2 }} width="100%">
                  <HandsFreeFeatures />
                </Box>
              </GridItem>
                
              <GridItem display="flex" justifyContent="center" alignItems="stretch">
                <AnimatedDealSummaryCard />
              </GridItem>
            </Grid>
          </Box>

          {/* Track Customer Engagement and Predict Churn */}
          <Box pt={16}>
            <VStack spacing={3} align="center" textAlign="center" mb={8}>
              <Flex
                bg="red.50"
                borderRadius="full"
                p={2}
                alignItems="center"
                justifyContent="center"
                boxSize="60px"
              >
                <Icon as={FaHeartbeat} color="red.500" boxSize={8} />
              </Flex>
              
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={headingColor}
              >
                Stop Churn Before It Starts
              </Heading>
              
              <Text
                fontSize={{ base: "md", md: "lg" }}
                maxW="800px"
                color={textColor}
                pb={6}
              >
                Proactively protect your customer relationships with intelligent, predictive engagement tracking.
              </Text>
            </VStack>
            
            {/* Analytics Side-by-side layout for features and visualization */}
            <Grid
              templateColumns={{ base: "1fr", lg: "0.9fr 1.1fr" }}
              gap={6}
              maxW="1200px"
              mx="auto"
              px={{ base: 2, md: 0 }}
            >
              <GridItem display="flex" alignItems="stretch">
                <Box flex="1" pr={{ lg: 2 }} width="100%">
                  <AnalyticsFeatures />
                </Box>
              </GridItem>
                
              <GridItem display="flex" justifyContent="center" alignItems="stretch">
                <Box flex="1" width={{ base: "100%", md: "100%" }} mt={{ base: 6, lg: 0 }}>
                  <ChurnAnalyticsVisual />
                </Box>
              </GridItem>
            </Grid>
          </Box>
        </VStack>
      </Container>
      
      {/* CSS for all styling including carousel and animations */}
      <style>{`
        .rotating-icon {
          animation: rotate 3s linear infinite;
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .svg-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .colored-svg {
          object-fit: contain;
          object-position: center;
          transition: transform 0.2s;
        }

        .svg-container:hover .colored-svg {
          transform: scale(1.1);
        }

        /* Animation for new activities */
        @keyframes highlight {
          0% { background-color: rgba(72, 187, 120, 0.2); }
          100% { background-color: transparent; }
        }
        
        /* Carousel styles */
        .scrollable-carousel {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          overflow-y: visible !important;
          overflow-x: auto;
          padding-left: 7.5%;
          padding-right: 7.5%;
        }
        
        .scrollable-carousel::-webkit-scrollbar {
          display: none;
        }
        
        .scrollable-carousel.no-transition {
          scroll-behavior: auto;
        }
        
        .carousel-item {
          transition: transform 0.3s ease;
        }
        
        .carousel-card-wrapper {
          padding-top: 3px;
          padding-bottom: 3px;
        }
        
        /* Snap-scrolling carousel styles */
        .snap-carousel-container {
          width: 100%;
          max-width: 100vw;
          overflow: hidden;
          position: relative;
          margin: 0 auto;
        }
        
        .snap-carousel {
          position: relative;
          width: 100%;
          overflow: hidden;
        }
        
        .snap-carousel-track {
          width: 100%;
        }
        
        .snap-carousel-item {
          transition: transform 0.3s;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 !important;
        }
        
        .snap-carousel-item:hover {
          transform: translateY(-3px);
        }
        
        .carousel-item-inner {
          width: 85%;
          max-width: 85%;
          margin: 0 auto;
        }
        
        @media (hover: hover) {
          .snap-carousel-nav {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          
          .snap-carousel:hover .snap-carousel-nav {
            opacity: 0.7;
          }
        }
        
        @media (max-width: 768px) {
          .scrollable-carousel {
            padding-bottom: 1rem;
          }
        }
      `}</style>
    </Box>
  );
};

export default LandingDealSection; 